import { useEffect, useRef, useState } from "react";
import { useAiChat } from "../../provider/AiChatProvider";
import AiChatMessage from "./AiChatMessage";
import { Box, Stack } from "@mui/material";
import {
  useAiEventStreamLifecycle,
  useAiPromptGenerationStream,
} from "../../hooks/useAiEventStream";
import { fetchCollection, fetchResource, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AiThinkingBubble from "./AiThinkingBubble";
import { AiChatMessage as AiChatMessageType } from "../../types/AIChat/AiChatMessage";
import { AiChatThread } from "../../types/AIChat/AiChatThread";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import { StreamingMarkdownText } from "../AiEventStream/StreamingMarkdownText";
import {
  getAiChatMessageVariants,
  getAiChatThreadPath,
  getLatestAiChatLeafUuid,
  refreshAiChatThread,
  selectAiChatBranch,
} from "../../services/aiChatService";
import { enqueueSnackbar } from "notistack";

export default function AiChatThreadMessages() {
  const { activeThreadId, isLoading } = useAiChat();
  const [isBranchSwitching, setIsBranchSwitching] = useState(false);
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const aiChatMessagesUri = "ai_chat_messages";
  const aiChatMessagesFilter = { aiChatThread: activeThreadId };
  const { data: aiChatMessagesResponse } = useQuery<HydraCollectionResult<AiChatMessageType>>({
    queryKey: queryKeys.collection(aiChatMessagesUri, aiChatMessagesFilter),
    queryFn: () => fetchCollection(aiChatMessagesUri, aiChatMessagesFilter),
    enabled: activeThreadId !== null,
  });
  const { data: activeThread } = useQuery<AiChatThread>({
    queryKey: queryKeys.item("ai_chat_threads", activeThreadId ?? 0),
    queryFn: () => fetchResource("ai_chat_threads", activeThreadId as number),
    enabled: activeThreadId !== null,
  });

  const allMessages = aiChatMessagesResponse?.["hydra:member"] ?? [];
  const currentMessages = getAiChatThreadPath(allMessages, activeThread?.currentLeafMessageUuid ?? null);
  const { text } = useAiPromptGenerationStream(activeThreadId);
  const { hasStreamStarted } = useAiEventStreamLifecycle(activeThreadId);

  const switchBranch = async (message: AiChatMessageType) => {
    if (activeThreadId === null || isLoading || isBranchSwitching) {
      return;
    }

    setIsBranchSwitching(true);
    try {
      await selectAiChatBranch(activeThreadId, getLatestAiChatLeafUuid(allMessages, message.uuid));
      await refreshAiChatThread(queryClient, activeThreadId);
    } catch {
      enqueueSnackbar("Der Gesprächszweig konnte nicht gewechselt werden.", { variant: "error" });
    } finally {
      setIsBranchSwitching(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [currentMessages.length, hasStreamStarted, text]);

  return (
    <>
      <Stack spacing={1}>
        {currentMessages.map((message) => (
          <AiChatMessage
            message={message}
            variants={getAiChatMessageVariants(allMessages, message)}
            onSelectVariant={switchBranch}
            isInteractionDisabled={isLoading || isBranchSwitching}
            key={message.uuid}
          />
        ))}
        {text && <StreamingMarkdownText text={text} />}
        {hasStreamStarted && <AiThinkingBubble />}
      </Stack>
      <Box ref={messagesEndRef} />
    </>
  );
}
